import React, { useState } from 'react';
import {
  Text,
  Alert,
  Loader,
  Table,
  Badge,
  Group,
  Title,
  Card,
  Stack,
  TextInput,
  Select,
  Avatar,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useAllGoals } from '../../hooks/useApi';
import { PageContainer } from '../../components/ui/PageContainer';
import { formatDate, formatDay, goalDeadlineDay } from '../../utils/date';
import { getGoalState, getGoalStatusColor, getGoalStatusText } from '../../utils/statusUtils';
import { useDevice } from '../../hooks/useDevice';
import { Goal } from '../../types';

const GOAL_STATES = ['pending', 'active', 'overdue', 'completed'] as const;

export const AllGoals: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: goals = [], isLoading, error } = useAllGoals();
  const deviceType = useDevice();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  // Unique assignees for filter
  const assignees = React.useMemo(() => {
    const map = new Map<string, string>();
    goals.forEach((goal) => {
      if (goal.assignee?._id && goal.assignee?.name) {
        map.set(goal.assignee._id, goal.assignee.name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [goals]);

  // Filter goals
  const filteredGoals = goals.filter((goal) => {
    const matchesSearch = !searchQuery || searchInGoal(goal, searchQuery);

    const matchesStatus = statusFilter === 'all' || getGoalState(goal) === statusFilter;

    const matchesAssignee = assigneeFilter === 'all' || goal.assignee?._id === assigneeFilter;

    return matchesSearch && matchesStatus && matchesAssignee;
  });

  if (error) {
    return (
      <PageContainer>
        <Alert icon={<IconAlertCircle size={16} />} title={t('messages.error')} color="red">
          {t('messages.errorLoading', { item: t('goals.title') })}
        </Alert>
      </PageContainer>
    );
  }

  const rows = filteredGoals.map((goal) => (
    <Table.Tr key={goal._id}>
      <Table.Td>
        <Stack gap={4}>
          <Text fw={500} size="sm">
            {goal.title}
          </Text>
          {goal.description && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {goal.description}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        {goal.assignee ? (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label={goal.assignee.email || goal.assignee.name}>
              <Avatar size="sm" radius="xl" src={goal.assignee.avatar}>
                {goal.assignee.name?.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
            <Text size="sm" lineClamp={1}>
              {goal.assignee.name}
            </Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {goal.assigner ? (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label={goal.assigner.email || goal.assigner.name}>
              <Avatar size="sm" radius="xl" src={goal.assigner.avatar}>
                {goal.assigner.name?.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
            <Text size="sm" lineClamp={1}>
              {goal.assigner.name}
            </Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={getGoalStatusColor(goal)} variant="light">
          {getGoalStatusText(goal, t)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {goal.deadline ? formatDay(goalDeadlineDay(goal), i18n.language) : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {goal.completedAt ? formatDate(goal.completedAt, i18n.language) : '—'}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PageContainer>
      <Group gap="sm" align="center" mb={{ base: 'md', sm: 'xl' }} wrap="wrap">
        <Title order={2} size="h3">
          {t('goals.allGoals')}
        </Title>
        <Badge color="teal" variant="light" size="sm">
          {filteredGoals.length}
          {deviceType !== 'mobile' && ` ${t('goals.title').toLowerCase()}`}
        </Badge>
      </Group>

      <Card withBorder radius="md" p={{ base: 'sm', sm: 'md' }}>
        <Stack gap="md" mb="md">
          <Group justify="space-between" gap="sm">
            {/* На телефоне фильтры переносятся: в одну строку «Исполнитель» сжимался до иконки. */}
            <Group gap="sm" style={{ flex: 1, minWidth: 0 }} wrap="wrap">
              <TextInput
                placeholder={t('common.search')}
                leftSection={<IconSearch size={14} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: '1 1 200px' }}
                size="sm"
              />
              <Select
                placeholder={t('common.status')}
                value={statusFilter === 'all' ? null : statusFilter}
                onChange={(value) => setStatusFilter(value || 'all')}
                data={GOAL_STATES.map((value) => ({ value, label: t(`goals.status.${value}`) }))}
                clearable
                size="sm"
                style={{ flex: '1 1 140px' }}
              />
              <Select
                placeholder={t('goals.assignee')}
                value={assigneeFilter === 'all' ? null : assigneeFilter}
                onChange={(value) => setAssigneeFilter(value || 'all')}
                data={assignees}
                clearable
                size="sm"
                searchable
                style={{ flex: '1 1 140px' }}
              />
            </Group>
          </Group>
        </Stack>

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : filteredGoals.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            {searchQuery || statusFilter !== 'all' || assigneeFilter !== 'all'
              ? t('common.noSearchResults')
              : t('goals.noActiveGoals')}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover miw={700}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 200 }}>{t('goals.title')}</Table.Th>
                  <Table.Th style={{ minWidth: 140 }}>{t('goals.assignee')}</Table.Th>
                  <Table.Th style={{ minWidth: 140 }}>{t('goals.assigner')}</Table.Th>
                  <Table.Th style={{ minWidth: 100 }}>{t('common.status')}</Table.Th>
                  <Table.Th style={{ minWidth: 100 }}>{t('goals.deadline')}</Table.Th>
                  <Table.Th style={{ minWidth: 100 }}>{t('goals.completedAt')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

function searchInGoal(goal: Goal, query: string): boolean {
  const lower = query.toLowerCase();
  return (
    goal.title.toLowerCase().includes(lower) ||
    goal.description?.toLowerCase().includes(lower) ||
    goal.assignee?.name?.toLowerCase().includes(lower) ||
    goal.assigner?.name?.toLowerCase().includes(lower) ||
    false
  );
}
